// OTA-safe tracking reliability controller.
//
// Goal: make remote-travel tracking honest and self-healing without any
// native/Swift/plist changes. Triggers a drain pipeline on the events that
// matter, and re-arms the active GPS watch if the persisted tracking state
// says ON but no watch is running in JS.
//
// Pipeline (idempotent, single-flight):
//   1. If persisted tracking is ON and no active watch is armed, restart
//      `startActiveWatch(tripId)` using the cached trip.
//   2. Drain the native LocationBG queue into the JS ping outbox for the
//      current trip (does nothing on web / pre-plugin builds).
//   3. If online, drain the JS ping outbox to the backend.
//
// Triggers:
//   - App boot (after main.tsx wires lifecycle).
//   - Capacitor App `appStateChange` → active.
//   - `@capacitor/network` `networkStatusChange` → connected.
//   - Window `mtsync:tracking-changed` with on=true.
//   - Periodic interval (90s) while tracking is ON and app is visible.
//
// No fake points are produced. Drains use the existing single-flight guards
// in `drainNativeLocationQueue` and `drainPingOutbox`, so duplicate events
// collapse into one in-flight pass.

import { Capacitor } from "@capacitor/core";
import { isNative } from "./platform";
import { getTrackingState, TRACKING_KEY } from "./tracking-state";
import { startActiveWatch, isActiveWatchRunning } from "./active-watch";
import { drainNativeLocationQueue, getNativeQueueStats, LocationBG, locationBGRegistered, type NativeQueueStats } from "./location-bg";
import { drainPingOutbox, getOutboxStats } from "./ping-outbox";
import { getCachedTripSync, hydrateCachedTrip } from "@/lib/offline/trip-cache";

const TICK_INTERVAL_MS = 30_000;
const TICK_MIN_GAP_MS = 5_000; // collapse storms (multiple triggers in 5s) into one tick
// Foreground reconnect watchdog: while the outbox has queued points and the
// tab is visible, *pull* @capacitor/network state every WATCHDOG_INTERVAL_MS
// instead of relying on the (often-missed) networkStatusChange event for the
// airplane-mode-OFF transition.
const WATCHDOG_INTERVAL_MS = 5_000;

let installed = false;
let inFlight: Promise<void> | null = null;
let lastTickAt = 0;
let visibleIntervalId: ReturnType<typeof setInterval> | null = null;
let watchdogIntervalId: ReturnType<typeof setInterval> | null = null;
let watchdogLastConnected: boolean | null = null;

async function resolveTripId(): Promise<string | null> {
  const sync = getCachedTripSync()?.id ?? null;
  if (sync) return sync;
  try {
    const t = await hydrateCachedTrip();
    return t?.id ?? null;
  } catch {
    return null;
  }
}

/**
 * Run a single drain + self-heal pass. Idempotent and single-flight; safe to
 * call from any trigger without coordinating between them.
 */
export const TRACKING_TICK_INTERVAL_MS = TICK_INTERVAL_MS;

function emitDrainEvent(detail: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent("mtsync:drain-complete", { detail }));
  } catch { /* ignore */ }
}

export function getLastTickAt(): number {
  return lastTickAt;
}

export function isDrainInFlight(): boolean {
  return inFlight != null;
}

export async function tickTrackingReliability(
  reason: string,
  opts?: { userInitiated?: boolean },
): Promise<void> {
  if (inFlight) return inFlight;
  const since = Date.now() - lastTickAt;
  if (since < TICK_MIN_GAP_MS && lastTickAt !== 0) {
    // Collapse rapid-fire triggers (foreground + network reconnect arriving
    // ~milliseconds apart). The most recent caller has already kicked one
    // pass, so this caller can no-op.
    return;
  }
  lastTickAt = Date.now();
  // Let the UI know a drain pass is starting so countdown can flip to syncing.
  emitDrainEvent({ reason, phase: "start" });
  const userInitiated = opts?.userInitiated === true;
  inFlight = (async () => {
    try {
      const trackingOn = await getTrackingState();
      const tripId = await resolveTripId();

      // Self-heal: persisted "on" but no JS watch armed → restart.
      if (trackingOn && tripId && !isActiveWatchRunning()) {
        try {
          // eslint-disable-next-line no-console
          console.info(`[tracking-reliability] self-heal: restarting active watch (reason=${reason} trip=${tripId.slice(0, 8)})`);
          await startActiveWatch(tripId);
        } catch (e) {
          console.warn("[tracking-reliability] self-heal startActiveWatch failed", e);
        }
      }

      // Always attempt a native-queue drain first — points captured on the
      // Swift side need to flow into the JS outbox before we try to upload.
      try {
        await drainNativeLocationQueue(tripId);
      } catch (e) {
        console.warn("[tracking-reliability] drainNativeLocationQueue failed", e);
      }

      // Then drain the outbox to the backend. drainPingOutbox does its own
      // reachability check and is a no-op when empty. Polling-reconnect /
      // tracking-on / manual triggers pass userInitiated so we bypass the
      // reachability cache (which may be stale "unreachable" after a long
      // offline stretch) and force a session refresh before the first POST.
      try {
        await drainPingOutbox(userInitiated ? { userInitiated: true } : undefined);
      } catch (e) {
        console.warn("[tracking-reliability] drainPingOutbox failed", e);
      }

      // Purge any points the native side has marked delivered so the
      // visible On-device queue count drops to 0 after a successful sync
      // (instead of waiting for the 500-point native auto-purge threshold).
      if (Capacitor.isNativePlatform() && locationBGRegistered()) {
        try {
          await LocationBG.clearDeliveredLocationPoints();
        } catch (e) {
          console.warn("[tracking-reliability] clearDeliveredLocationPoints failed", e);
        }
      }
    } finally {
      inFlight = null;
      lastTickAt = Date.now();
      emitDrainEvent({ reason, phase: "complete" });
    }
  })();
  return inFlight;
}

function startVisibleInterval() {
  if (visibleIntervalId != null) return;
  if (typeof window === "undefined") return;
  visibleIntervalId = setInterval(() => {
    if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
    void tickTrackingReliability("interval");
  }, TICK_INTERVAL_MS);
}

function stopVisibleInterval() {
  if (visibleIntervalId != null) {
    clearInterval(visibleIntervalId);
    visibleIntervalId = null;
  }
}

/**
 * Foreground reconnect watchdog. While the outbox has queued points and the
 * tab is visible, *pulls* @capacitor/network state every 5s. When we see a
 * false→true edge, fires a userInitiated tick — bypassing the reachability
 * cache, refreshing the session, and posting immediately. This is the fix for
 * the iOS airplane-mode-OFF case where networkStatusChange never fires.
 */
function startWatchdog() {
  if (watchdogIntervalId != null) return;
  if (typeof window === "undefined") return;
  if (!Capacitor.isNativePlatform()) return;
  let lastForcedAt = 0;
  const FORCE_GAP_MS = 30_000;
  watchdogIntervalId = setInterval(() => {
    if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
    const queued = getOutboxStats().queued;
    if (queued <= 0) {
      watchdogLastConnected = null;
      return;
    }
    void (async () => {
      try {
        const { Network } = await import("@capacitor/network");
        const s = await Network.getStatus();
        const prev = watchdogLastConnected;
        watchdogLastConnected = !!s.connected;
        if (!s.connected) return;

        if (prev === false) {
          // eslint-disable-next-line no-console
          console.info(`[tracking-reliability] watchdog reconnect edge (queued=${queued}) → userInitiated drain`);
          lastForcedAt = Date.now();
          void tickTrackingReliability("polling-reconnect", { userInitiated: true });
          return;
        }
        if (prev == null) {
          // eslint-disable-next-line no-console
          console.info(`[tracking-reliability] watchdog first-look online with queued=${queued} → userInitiated drain`);
          lastForcedAt = Date.now();
          void tickTrackingReliability("polling-online", { userInitiated: true });
          return;
        }
        // Steady-state: link is up and queue still hasn't drained. Force a
        // userInitiated tick (bypasses cached "unreachable" reachability,
        // refreshes session) every FORCE_GAP_MS so an LTE connection that
        // came up without a clean networkStatusChange edge still drains
        // instead of sitting behind a stale reachability cache.
        if (Date.now() - lastForcedAt > FORCE_GAP_MS) {
          // eslint-disable-next-line no-console
          console.info(`[tracking-reliability] watchdog steady-online with queued=${queued} → userInitiated drain`);
          lastForcedAt = Date.now();
          void tickTrackingReliability("polling-steady", { userInitiated: true });
        }
      } catch { /* ignore */ }
    })();
  }, WATCHDOG_INTERVAL_MS);
}

/**
 * Install all triggers. Idempotent — safe to call multiple times. Should be
 * called once at boot from main.tsx after the rest of app-lifecycle wiring.
 */
export function installTrackingReliability(): void {
  if (installed) return;
  installed = true;

  // Initial pass shortly after boot so a relaunch immediately drains anything
  // the native queue accumulated while the app was suspended.
  setTimeout(() => { void tickTrackingReliability("boot"); }, 1500);

  if (typeof window !== "undefined") {
    // Tracking turned on from any screen → kick the pipeline immediately.
    window.addEventListener("mtsync:tracking-changed", (e: Event) => {
      const detail = (e as CustomEvent<{ on: boolean }>).detail;
      if (detail?.on) void tickTrackingReliability("tracking-on");
    });
    // Storage event (other tabs / web) — best-effort.
    window.addEventListener("storage", (e: StorageEvent) => {
      if (e.key === TRACKING_KEY && e.newValue === "1") {
        void tickTrackingReliability("tracking-on-storage");
      }
    });
    // Visibility resume on web/native — covers PWA + WebView resume paths
    // even if Capacitor's App listener is delayed.
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        void tickTrackingReliability("visibility");
      }
    });
    // Online event for web fallback.
    window.addEventListener("online", () => { void tickTrackingReliability("online"); });
  }

  // Periodic interval while the app is visible.
  startVisibleInterval();
  // Foreground watchdog (pulls Network state; doesn't trust the listener).
  startWatchdog();

  // Native triggers: Capacitor App + Network. These run in addition to the
  // existing app-lifecycle handlers — both call the same single-flight tick,
  // so duplicates collapse.
  if (Capacitor.isNativePlatform()) {
    void import("@capacitor/app")
      .then(({ App }) => {
        App.addListener("appStateChange", ({ isActive }) => {
          if (isActive) void tickTrackingReliability("appActive");
        }).catch(() => { /* ignore */ });
      })
      .catch(() => { /* ignore */ });

    void import("@capacitor/network")
      .then(({ Network }) => {
        Network.addListener("networkStatusChange", (s) => {
          if (s.connected) void tickTrackingReliability("networkReconnect");
        }).catch(() => { /* ignore */ });
      })
      .catch(() => { /* ignore */ });
  }
}

// --------------------------------------------------------------------------
// React hook for the status UI: native queue count + last-seen timestamps.
// Pure read; polls every 10s while mounted. Returns null on web / pre-plugin.
// --------------------------------------------------------------------------

import { useEffect, useState } from "react";

export function useNativeQueueStats(intervalMs = 2_000): NativeQueueStats | null {
  const [stats, setStats] = useState<NativeQueueStats | null>(null);
  useEffect(() => {
    if (!isNative()) return;
    let alive = true;
    const read = async () => {
      try {
        const s = await getNativeQueueStats();
        if (alive) setStats(s);
      } catch { /* ignore */ }
    };
    void read();
    const id = setInterval(() => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      void read();
    }, Math.max(1000, intervalMs));
    // Refresh immediately whenever a drain pass completes so the UI doesn't
    // wait for the next poll to reflect the work that just happened.
    const onDrain = () => { void read(); };
    if (typeof window !== "undefined") {
      window.addEventListener("mtsync:drain-complete", onDrain);
    }
    return () => {
      alive = false;
      clearInterval(id);
      if (typeof window !== "undefined") {
        window.removeEventListener("mtsync:drain-complete", onDrain);
      }
    };
  }, [intervalMs]);
  return stats;
}

/**
 * Countdown to the next scheduled drain tick. Returns `{ secondsLeft, syncing }`.
 * `secondsLeft` is 0 while a drain is in flight or no tick has run yet.
 * Updates once per second; pauses when the tab is hidden.
 */
export function useNextSyncCountdown(): { secondsLeft: number; syncing: boolean } {
  const [state, setState] = useState(() => computeCountdown());
  useEffect(() => {
    let alive = true;
    const tick = () => {
      if (!alive) return;
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      setState(computeCountdown());
    };
    const id = setInterval(tick, 1000);
    const onDrain = () => tick();
    if (typeof window !== "undefined") {
      window.addEventListener("mtsync:drain-complete", onDrain);
    }
    return () => {
      alive = false;
      clearInterval(id);
      if (typeof window !== "undefined") {
        window.removeEventListener("mtsync:drain-complete", onDrain);
      }
    };
  }, []);
  return state;
}

function computeCountdown(): { secondsLeft: number; syncing: boolean } {
  if (isDrainInFlight()) return { secondsLeft: 0, syncing: true };
  const last = getLastTickAt();
  if (!last) return { secondsLeft: 0, syncing: false };
  const next = last + TICK_INTERVAL_MS;
  const left = Math.max(0, Math.ceil((next - Date.now()) / 1000));
  return { secondsLeft: left, syncing: false };
}

